import { Row, message } from 'antd';
import FormList from 'antd/lib/form/FormList';
import { useMemo } from 'react';

import {
  Collapse,
  Form,
  FormItemInputNumber,
  GenericDrawerHeader,
  Title,
} from '../../../../components/index';
import {
  useUpdateCargoStorageMerchantTotalMovimentedMutation,
  useUpdateLinesMovimentationMutation,
} from '../../../../services/movimentationApi';
import { OperationalOperationType, Stopover } from '../../../../types';
import { OperationLineDescription } from '../operationLineDescripion';
import {
  formatCargoStorageMerchantLineMovimentationToSave,
  formatOperationLineMovimentationToSave,
} from './formatters';

type LiquidBulkOperationMovimentationProps = {
  operation?: OperationalOperationType;
  onCloseDrawer?: (show: boolean) => void;
  selectedStopover?: Stopover;
};

const { Panel } = Collapse;

export function LiquidBulkOperationMovimentation(
  props: LiquidBulkOperationMovimentationProps
) {
  const { operation, onCloseDrawer, selectedStopover } = props;

  const [form] = Form.useForm();

  const maxTolerancePercent = Number(
    process.env.REACT_APP_MAX_OPERATION_TOLERANCE_PERCENT || 20
  );
  const toleranceFactor = 1 + maxTolerancePercent / 100;

  const [
    updateLinesMovimentations,
    { isLoading: isLoadingUpdateMovimentations },
  ] = useUpdateLinesMovimentationMutation();
  const [updateCargoStorageMerchantTotalMovimented] =
    useUpdateCargoStorageMerchantTotalMovimentedMutation();

  const totalOperated = useMemo(() => {
    const total2 = operation?.operation_lines?.flatMap(
      (operationLine) => operationLine.movimentation
    );
    const total = total2?.flat(1).reduce((previous, current) => {
      if (current) {
        previous += current.gross_flow || 0;
      }
      return previous;
    }, 0);
    if (total && total > 0) {
      return `Total operado: ${total} ton`;
    }
    return 'Não operado';
  }, [operation]);

  function handleFormSubmit() {
    form
      .validateFields()
      .then(async (values) => {
        updateLinesMovimentations({
          movimentations: formatOperationLineMovimentationToSave(
            values.operationLines
          ),
          operation_id: operation?.id,
        }).then(() => {
          updateCargoStorageMerchantTotalMovimented(
            formatCargoStorageMerchantLineMovimentationToSave(
              values.operationLines
            )
          );
        });
      })
      .finally(() => {
        message.success('Movimentação atualizada com sucesso!', 5);
      });
  }

  function closeDrawer() {
    if (onCloseDrawer) {
      onCloseDrawer(false);
    }
  }

  function getHeader() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <strong style={{ fontSize: '16px' }}>Registrar movimentação</strong>
        <span style={{ fontWeight: '400', color: 'var(--neutral_medium)' }}>
          {totalOperated}
        </span>
      </div>
    );
  }

  function getHeaderOperationLine(
    lineName: string,
    operationProductId: number
  ) {
    const operationProductCargoTypeName =
      operation?.items?.find((item) => item.id === operationProductId)
        ?.cargo_type?.name || 'Produto não informado';
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'baseline',
        }}
      >
        <b>{lineName}</b>
        <span
          style={{
            color: 'var(--neutral_medium)',
            fontWeight: '400',
            fontSize: '14px',
          }}
        >
          {operationProductCargoTypeName}
        </span>
      </div>
    );
  }

  return (
    <Form
      form={form}
      name="movimentation"
      initialValues={{ operationLines: operation?.operation_lines }}
      onFinish={handleFormSubmit}
      layout="vertical"
    >
      <GenericDrawerHeader
        title={getHeader()}
        formName="movimentation"
        showBackButton
        onBack={closeDrawer}
        isLoadingSave={isLoadingUpdateMovimentations}
      />
      <FormList name="operationLines">
        {(fields) => (
          <Collapse expandIconPosition="end" destroyInactivePanel={false}>
            {fields.map((field) => {
              const { key } = field;
              const keyStr = String(key);
              return (
                <Panel
                  header={getHeaderOperationLine(
                    form.getFieldValue([
                      'operationLines',
                      keyStr,
                      'line',
                      'name',
                    ]),
                    form.getFieldValue([
                      'operationLines',
                      keyStr,
                      'operation_product_id',
                    ])
                  )}
                  key={`operation_line_${keyStr}`}
                  forceRender
                >
                  <OperationLineDescription
                    operationLine={form.getFieldValue([
                      'operationLines',
                      keyStr,
                    ])}
                    hideProductAndMovimentation
                  />
                  {selectedStopover?.cargos_storage_merchant?.map(
                    (cargoStorageMerchant, index) => {
                      return (
                        <Row gutter={16}>
                          <Title>
                            CE {cargoStorageMerchant.merchant_ce__code} - NCM{' '}
                            {cargoStorageMerchant.ncm_code}{' '}
                            {cargoStorageMerchant.ncm_description}
                            {(() => {
                              try {
                                const matchingStorage = operation?.items
                                  ?.flatMap(
                                    (item) => item.operation_manifests || []
                                  )
                                  ?.flatMap(
                                    (manifest) => manifest.merchants_ce || []
                                  )
                                  ?.filter(
                                    (merchant) =>
                                      merchant.code ===
                                      cargoStorageMerchant.merchant_ce__code
                                  )
                                  ?.flatMap(
                                    (merchant) => merchant.storages || []
                                  )
                                  ?.find((storage) => {
                                    // Match by cargo ID
                                    return storage.cargos_storage?.some(
                                      (cargo) => {
                                        const cargoId = cargo.id?.toString();
                                        const merchantId =
                                          cargoStorageMerchant.id?.toString();
                                        return cargoId === merchantId;
                                      }
                                    );
                                  });

                                const terminalName =
                                  matchingStorage?.storage_company?.name;

                                const ceMerchantWeight =
                                  cargoStorageMerchant.weight;

                                let result = '';
                                if (terminalName) {
                                  result += ` - Área de Armazenamento: ${terminalName}`;
                                }
                                if (ceMerchantWeight) {
                                  result += ` - Peso CE Mercante: ${ceMerchantWeight.toLocaleString(
                                    'pt-BR'
                                  )} ton`;
                                }
                                return result;
                              } catch (error) {
                                return '';
                              }
                            })()}
                          </Title>
                          <FormItemInputNumber
                            colSpan={12}
                            name={[
                              keyStr,
                              'movimentation',
                              index,
                              'gross_flow',
                            ]}
                            label="Peso movimentado"
                            addonAfter="ton"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (
                                    value &&
                                    cargoStorageMerchant &&
                                    cargoStorageMerchant.weight
                                  ) {
                                    const maxAllowed =
                                      cargoStorageMerchant.weight *
                                      toleranceFactor;
                                    if (value > maxAllowed) {
                                      return Promise.reject(
                                        new Error(
                                          `Excedeu o limite de ${maxTolerancePercent}% (máx: ${maxAllowed.toLocaleString(
                                            'pt-BR'
                                          )} ton)`
                                        )
                                      );
                                    }
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          />
                          <Form.Item
                            name={[keyStr, 'movimentation', index, 'id']}
                            noStyle
                          />
                          {/* <Form.Item name={[keyStr, 'id']} noStyle /> */}
                          {/* <Form.Item
                            name={[keyStr, 'movimentation', 'id']}
                            noStyle
                          /> */}
                          <Form.Item
                            name={[
                              keyStr,
                              'movimentation',
                              index,
                              'cargo_storage_merchant_id',
                            ]}
                            initialValue={cargoStorageMerchant.id}
                            noStyle
                          />
                        </Row>
                      );
                    }
                  )}
                </Panel>
              );
            })}
          </Collapse>
        )}
      </FormList>
    </Form>
  );
}
